import { getAuth } from "firebase/auth";

import FirebaseAdapter from "@wc/adapters/firebase";
import { RolesEnum, roleToNumeric } from "@wc/domain/authz";

/**
 * Add a user to an organization with a specific role
 * @param {object} params The pair of user and org ids
 * @param {string} params.userId The user id
 * @param {string} params.organizationId The org id
 * @param {string} params.role A RolesEnum role
 * @returns {void} Nothing
 */
async function addUserWithRole({ userId, organizationId, role }) {
  await FirebaseAdapter().membership.addUserToOrg(userId, organizationId);

  await FirebaseAdapter().roles.assignOrgRole(
    userId,
    organizationId,
    roleToNumeric(role),
    userId,
  );
}

/**
 * When creating a new user, we'll give them an organization that's just them
 * @param {object} ids The pair of user and org ids
 * @param {string} ids.userId The user id
 * @param {string} ids.organizationId The org id
 * @returns {void} Nothing
 */
export async function addUserToOwnOrg({ userId, organizationId }) {
  await addUserWithRole({ userId, organizationId, role: RolesEnum.ORG_ADMIN });
}

/**
 * Load an organization by ID, verifying the user is a member via claims.
 * @param {string} orgId The ID of the organization
 * @param {string} _userId The id of the user trying to access (kept for API compatibility)
 * @returns {Promise<object>} The organization
 * @throws {Error} If org not found or user doesn't have access
 */
export async function orgIfMemberById(orgId, _userId) {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("No authenticated user");
  }

  const tokenResult = await currentUser.getIdTokenResult();
  const userOrgIds = Object.keys(tokenResult.claims.orgs || {});

  if (!userOrgIds.includes(orgId)) {
    throw new Error(`Unable to access Org ${orgId}`);
  }

  const org = await FirebaseAdapter().organizations.getOrganization(orgId);

  if (!org) {
    throw new Error(`Organization ${orgId} not found`);
  }

  if (org.createdAt?.toDate) {
    org.createdAt = org.createdAt.toDate();
  }

  return org;
}
